import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { 
  Plus, MapPin, Calendar, Clock, Star, ChevronDown, ChevronUp, 
  Loader2, AlertCircle, CheckCircle2, Plane, Send, Globe, 
  Wallet, FileText
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { NestPointsDisplay } from "@/components/dashboard/NestPointsDisplay";
import nestPointsIcon from "@/assets/nest-points-icon.png";

interface Employee {
  id: string;
  company_id: string;
  current_country?: string;
  companies: {
    name: string;
  };
}

interface Profile {
  first_name: string;
  last_name: string;
  residence_country?: string;
}

interface Points {
  total_points: number;
  used_points: number;
}

interface AvailableCountry {
  country_id: string;
  country_name: string;
  image_url: string | null;
  flag_url: string | null;
  banner_url: string | null;
  tagline: string | null;
  timezone_utc_offset: string | null;
  ease_compliance: number | null;
  cost_of_living: number | null;
  attractiveness_rating: number | null;
  tourist_stay_default: string | null;
  remote_work_visa_option: string | null;
  key_requirements: string | null;
  employee_notes: string | null;
  points_per_week_override: number | null;
  distance_category: string | null;
  timezone_offset_hours: number | null;
  base_points_per_week: number;
  company_headquarters_timezone: string | null;
}

interface PointsCalculation {
  allowed: boolean;
  total_points?: number;
  points_per_week?: number;
  duration_weeks?: number;
  timezone_difference?: number;
  distance_category?: string;
  multiplier?: number;
  can_afford?: boolean;
  current_balance?: number;
  remaining_after?: number;
  reason?: string;
  policy_name?: string;
}

interface RemoteWorkRequestDialogProps {
  employee: Employee | null;
  profile: Profile | null;
  currentPoints: Points | null;
  onRequestSubmitted: () => void;
}

const RemoteWorkRequestDialog = ({ employee, profile, currentPoints, onRequestSubmitted }: RemoteWorkRequestDialogProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [countries, setCountries] = useState<AvailableCountry[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [comment, setComment] = useState("");
  const [isCountryDetailsOpen, setIsCountryDetailsOpen] = useState(false);
  const [pointsCalculation, setPointsCalculation] = useState<PointsCalculation | null>(null);
  const [calculatingPoints, setCalculatingPoints] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const availablePoints = currentPoints ? currentPoints.total_points - currentPoints.used_points : 0;
  const selectedCountry = useMemo(() => 
    countries.find(c => c.country_id === selectedCountryId), 
    [countries, selectedCountryId]
  );

  // Fetch available countries when dialog opens
  useEffect(() => {
    if (isDialogOpen && employee?.id) {
      fetchAvailableCountries();
    }
  }, [isDialogOpen, employee?.id]);

  // Calculate points when country or dates change
  useEffect(() => {
    if (selectedCountryId && startDate && endDate && employee) {
      calculatePoints();
    } else {
      setPointsCalculation(null);
    }
  }, [selectedCountryId, startDate, endDate]);

  const fetchAvailableCountries = async () => {
    if (!employee?.id) return;
    setLoadingCountries(true);
    try {
      const { data, error } = await (supabase.rpc as any)('get_employee_available_countries', {
        p_employee_id: employee.id
      });

      if (error) throw error;
      setCountries(data || []);
    } catch (error: any) {
      console.error('Error fetching countries:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les destinations disponibles"
      });
    } finally {
      setLoadingCountries(false);
    }
  };

  const calculateDuration = () => {
    if (!startDate || !endDate) return { days: 0, weeks: 0 };
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    const weeks = Math.ceil(days / 7);
    return { days, weeks };
  };

  const calculatePoints = async () => {
    if (!employee?.company_id || !selectedCountryId) return;
    
    const { weeks } = calculateDuration();
    if (weeks < 1) return;

    setCalculatingPoints(true);
    try {
      const { data, error } = await supabase.functions.invoke('calculate-nest-points', {
        body: {
          company_id: employee.company_id,
          destination_country_id: selectedCountryId,
          duration_weeks: weeks,
          employee_id: employee.id
        }
      });

      if (error) throw error;
      setPointsCalculation(data);
    } catch (error: any) {
      console.error('Error calculating points:', error);
      setPointsCalculation(null);
    } finally {
      setCalculatingPoints(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const handleSubmit = async () => {
    if (!selectedCountryId || !startDate || !endDate || !employee || !pointsCalculation) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires"
      });
      return;
    }

    if (!pointsCalculation.can_afford) {
      toast({
        variant: "destructive",
        title: "Points insuffisants",
        description: `Cette demande nécessite ${pointsCalculation.total_points} points, mais vous n'avez que ${availablePoints} points disponibles.`
      });
      return;
    }

    setSubmitting(true);
    try {
      const { days, weeks } = calculateDuration();
      
      const { data: requestData, error: requestError } = await supabase
        .from('remote_work_requests')
        .insert({
          employee_id: employee.id,
          company_id: employee.company_id,
          destination_country: selectedCountry?.country_name || '',
          start_date: startDate,
          end_date: endDate,
          duration_weeks: weeks,
          requested_points: pointsCalculation.total_points || 0,
          points_per_week: pointsCalculation.points_per_week || 0,
          distance_category: pointsCalculation.distance_category || null,
          timezone_difference_hours: pointsCalculation.timezone_difference || null,
          comment: comment || null,
          status: 'pending',
          manager_approval_status: 'pending',
          hr_approval_status: 'pending'
        })
        .select('id')
        .single();

      if (requestError) throw requestError;

      // Send notification to manager via edge function (reliable - bypasses RLS)
      const { data: empData } = await supabase
        .from('employees')
        .select('manager_profile_id')
        .eq('id', employee.id)
        .single();

      if (empData?.manager_profile_id) {
        const { error: notifyError } = await supabase.functions.invoke('send-workflow-notification', {
          body: {
            type: 'employee_request_submitted',
            request_id: requestData.id,
            recipient_profile_id: empData.manager_profile_id,
            employee_name: `${profile?.first_name} ${profile?.last_name}`,
            destination_country: selectedCountry?.country_name || '',
            start_date: startDate,
            end_date: endDate
          }
        });
        
        if (notifyError) {
          console.error('Failed to notify manager:', notifyError);
        }
      }

      toast({
        title: "Demande envoyée ! 🎉",
        description: `Votre demande pour ${selectedCountry?.country_name} a été transmise à votre manager.`
      });

      // Reset form and close dialog
      setIsDialogOpen(false);
      resetForm();
      onRequestSubmitted();
    } catch (error: any) {
      console.error('Error submitting request:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'envoyer la demande"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedCountryId("");
    setStartDate("");
    setEndDate("");
    setComment("");
    setPointsCalculation(null);
    setIsCountryDetailsOpen(false);
  };

  const renderStars = (rating: number | null, max: number = 5) => {
    if (rating === null) return <span className="text-muted-foreground text-sm">Non évalué</span>;
    return (
      <div className="flex gap-0.5">
        {Array.from({ length: max }).map((_, i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${i < rating ? 'fill-warning text-warning' : 'text-muted-foreground/30'}`}
          />
        ))}
      </div>
    );
  };

  const getDistanceBadge = (category: string | null, tzDiff: number | null) => {
    if (!category) return null;
    const configs: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
      'same_timezone': { label: 'Même fuseau', variant: 'default' },
      'near': { label: `±${tzDiff || 2}h`, variant: 'secondary' },
      'medium': { label: `±${tzDiff || 4}h`, variant: 'secondary' },
      'far': { label: `±${tzDiff || 6}h+`, variant: 'outline' }
    };
    const config = configs[category] || { label: category, variant: 'outline' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const { days, weeks } = calculateDuration();
  const canSubmit = selectedCountryId && startDate && endDate && pointsCalculation?.can_afford && !submitting;

  return (
    <Dialog open={isDialogOpen} onOpenChange={(open) => {
      setIsDialogOpen(open);
      if (!open) resetForm();
    }}>
      <DialogTrigger asChild>
        <Button 
          size="lg" 
          className="w-full sm:w-auto px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg font-bold shadow-lg hover:shadow-xl transition-all"
        >
          <Plus className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 flex-shrink-0" />
          <span>Nouvelle demande de Workation</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl sm:text-2xl flex items-center gap-2">
            <Plane className="h-6 w-6 text-primary" />
            Nouvelle demande de Workation
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* Points Balance - Prominent Display */}
          <Card className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-primary/20">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-full bg-primary/10">
                    <img src={nestPointsIcon} alt="Nest Points" className="h-8 w-8" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Votre solde disponible</p>
                    <p className="text-3xl font-bold text-primary">{availablePoints.toLocaleString()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Nest Points</p>
                  <Progress 
                    value={currentPoints ? ((currentPoints.total_points - currentPoints.used_points) / currentPoints.total_points) * 100 : 0}
                    className="w-24 h-2 mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Country Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base font-medium">
              <Globe className="h-4 w-4 text-primary" />
              Destination *
            </Label>
            {loadingCountries ? (
              <div className="flex items-center gap-2 p-4 border rounded-lg bg-muted/50">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Chargement des destinations...</span>
              </div>
            ) : countries.length === 0 ? (
              <div className="flex items-center gap-2 p-4 border rounded-lg bg-destructive/10 border-destructive/20">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">Aucune destination n'est ouverte par votre entreprise.</span>
              </div>
            ) : (
              <Select value={selectedCountryId} onValueChange={(value) => {
                setSelectedCountryId(value);
                setIsCountryDetailsOpen(true);
              }}>
                <SelectTrigger className="w-full h-auto py-3">
                  <SelectValue placeholder="Choisir une destination..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {countries.map((country) => (
                    <SelectItem key={country.country_id} value={country.country_id} className="py-3">
                      <div className="flex items-center gap-3 w-full">
                        {country.flag_url ? (
                          <img src={country.flag_url} alt="" className="h-5 w-7 object-cover rounded shadow-sm" />
                        ) : (
                          <div className="h-5 w-7 bg-muted rounded flex items-center justify-center">
                            <MapPin className="h-3 w-3" />
                          </div>
                        )}
                        <span className="font-medium">{country.country_name}</span>
                        <span className="text-muted-foreground ml-auto text-sm">
                          {country.points_per_week_override || country.base_points_per_week} pts/sem
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Country Details */}
          {selectedCountry && (
            <Collapsible open={isCountryDetailsOpen} onOpenChange={setIsCountryDetailsOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                  <span className="text-sm font-medium text-muted-foreground">
                    Détails de {selectedCountry.country_name}
                  </span>
                  {isCountryDetailsOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <Card className="overflow-hidden">
                  {selectedCountry.banner_url && (
                    <div className="h-32 w-full overflow-hidden">
                      <img 
                        src={selectedCountry.banner_url} 
                        alt={selectedCountry.country_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="pt-4 space-y-4">
                    {selectedCountry.tagline && (
                      <p className="text-sm italic text-muted-foreground">{selectedCountry.tagline}</p>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Facilité de conformité</p>
                        {renderStars(selectedCountry.ease_compliance)}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Coût de la vie</p>
                        {renderStars(selectedCountry.cost_of_living)}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Attractivité</p>
                        {renderStars(selectedCountry.attractiveness_rating)}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Fuseau horaire</p>
                        <p className="text-sm font-medium">{selectedCountry.timezone_utc_offset || 'N/A'}</p>
                      </div>
                    </div>

                    {(selectedCountry.tourist_stay_default || selectedCountry.remote_work_visa_option) && (
                      <div className="space-y-2 pt-2 border-t">
                        {selectedCountry.tourist_stay_default && (
                          <div className="flex items-start gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Séjour touristique</p>
                              <p className="text-sm">{selectedCountry.tourist_stay_default}</p>
                            </div>
                          </div>
                        )}
                        {selectedCountry.remote_work_visa_option && (
                          <div className="flex items-start gap-2">
                            <Globe className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Visa télétravail</p>
                              <p className="text-sm">{selectedCountry.remote_work_visa_option}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedCountry.employee_notes && (
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground mb-1">Notes pour les employés</p>
                        <p className="text-sm">{selectedCountry.employee_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>
          )}

          <Separator />

          {/* Date Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base font-medium">
              <Calendar className="h-4 w-4 text-primary" />
              Période *
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date" className="text-sm text-muted-foreground">Date de début</Label>
                <Input
                  id="start_date"
                  type="date"
                  min={getMinDate()}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="end_date" className="text-sm text-muted-foreground">Date de fin</Label>
                <Input
                  id="end_date"
                  type="date"
                  min={startDate || getMinDate()}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            {startDate && endDate && (
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>
                  <strong>{days}</strong> jour{days > 1 ? 's' : ''} ({weeks} semaine{weeks > 1 ? 's' : ''})
                </span>
              </div>
            )}
          </div>

          {/* Points Calculator */}
          {(selectedCountryId && startDate && endDate) && (
            <Card className={`border-2 transition-colors ${
              pointsCalculation?.can_afford 
                ? 'border-success/50 bg-success/5' 
                : pointsCalculation?.can_afford === false 
                  ? 'border-destructive/50 bg-destructive/5'
                  : 'border-border'
            }`}>
              <CardContent className="pt-4">
                {calculatingPoints ? (
                  <div className="flex items-center justify-center gap-2 py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-sm">Calcul des points en cours...</span>
                  </div>
                ) : pointsCalculation ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wallet className="h-5 w-5 text-primary" />
                        <span className="font-medium">Coût de la demande</span>
                      </div>
                      {getDistanceBadge(pointsCalculation.distance_category || null, pointsCalculation.timezone_difference || null)}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Points par semaine</p>
                        <p className="text-lg font-semibold">{pointsCalculation.points_per_week}</p>
                        {pointsCalculation.multiplier && pointsCalculation.multiplier !== 1 && (
                          <p className="text-xs text-muted-foreground">
                            (×{pointsCalculation.multiplier} - {pointsCalculation.distance_category})
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total requis</p>
                        <p className="text-2xl font-bold text-primary">{pointsCalculation.total_points}</p>
                      </div>
                    </div>

                    <Separator />

                    <div className={`flex items-center gap-2 p-3 rounded-lg ${
                      pointsCalculation.can_afford 
                        ? 'bg-success/10 text-success' 
                        : 'bg-destructive/10 text-destructive'
                    }`}>
                      {pointsCalculation.can_afford ? (
                        <>
                          <CheckCircle2 className="h-5 w-5" />
                          <div>
                            <p className="font-medium">Vous pouvez partir ! ✨</p>
                            <p className="text-sm opacity-80">
                              Il vous restera {pointsCalculation.remaining_after} points après cette demande
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-5 w-5" />
                          <div>
                            <p className="font-medium">Points insuffisants</p>
                            <p className="text-sm opacity-80">
                              Il vous manque {(pointsCalculation.total_points || 0) - availablePoints} points
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment" className="text-sm text-muted-foreground">
              Commentaire (optionnel)
            </Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Informations complémentaires pour votre manager..."
              rows={2}
            />
          </div>

          <Separator />

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setIsDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button 
              className="flex-1 gap-2"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Envoyer au manager
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RemoteWorkRequestDialog;
